import React from "react";
import { Box, Typography, Button, IconButton, alpha } from "@mui/material";
import {
  AutoAwesome,
  ArrowBackIosNew,
  ArrowForwardIos,
  FiberManualRecord,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { CMSHeroSection } from "../services/cms";
import { resolveImageUrl, withImageFallback } from "../utils/imageFallback";

const DEFAULT_PRODUCTS_LISTING_PATH = "/category/all";

const resolveHeroCtaLink = (link?: string | null): string => {
  const trimmed = link?.trim();
  if (!trimmed || trimmed === "#products-grid") {
    return DEFAULT_PRODUCTS_LISTING_PATH;
  }
  return trimmed;
};

type ProductsHeroProps = {
  heroSlides: CMSHeroSection[];
  activeSlide: number;
  isPaused: boolean;
  isTransitioning: boolean;
  isDark: boolean;
  backgroundDefault: string;
  backgroundPaper: string;
  textPrimary: string;
  onPauseChange: (paused: boolean) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onPrevSlide: () => void;
  onNextSlide: () => void;
  onSlideChange: (index: number) => void;
  exploreProductsLabel: string;
};

const ProductsHero: React.FC<ProductsHeroProps> = ({
  heroSlides,
  activeSlide,
  isPaused,
  isTransitioning,
  isDark,
  backgroundDefault,
  backgroundPaper,
  textPrimary,
  onPauseChange,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onPrevSlide,
  onNextSlide,
  onSlideChange,
  exploreProductsLabel,
}) => {
  const navigate = useNavigate();
  const heroFallbackImage = "/ahmed-OjlYsOIq-TQ-unsplash-1920x768.jpg";
  const currentSlide = heroSlides[activeSlide];
  const activeColor = currentSlide?.gradient_start || "#00d4ff";
  const activeGradientEnd = currentSlide?.gradient_end || "#0099cc";
  const ctaLink = resolveHeroCtaLink(currentSlide?.cta_link);
  const isExternalLink = /^https?:\/\//i.test(ctaLink);
  const isHashLink = ctaLink.startsWith("#");

  const handleHashCtaClick = () => {
    const target = document.querySelector(ctaLink);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
      return;
    }
    navigate(DEFAULT_PRODUCTS_LISTING_PATH);
  };

  if (!heroSlides.length) {
    return null;
  }

  return (
    <Box
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured medical equipment"
      onMouseEnter={() => onPauseChange(true)}
      onMouseLeave={() => onPauseChange(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      sx={{
        position: "relative",
        height: { xs: 400, sm: 350, md: 400, lg: 450 },
        mb: { xs: 2, md: 4 },
        borderRadius: { xs: "0px", sm: "8px" },
        overflow: "hidden",
        border: isDark
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.3)"
          : "0 10px 30px rgba(0,0,0,0.1)",
        background: backgroundDefault,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: alpha(activeColor, 0.05),
          zIndex: 3,
          pointerEvents: "none",
        },
      }}
    >
      {heroSlides.map((slide, index) => (
        <Box
          key={slide.id || index}
          aria-hidden={activeSlide !== index}
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: slide.background_image
              ? `url(${resolveImageUrl(slide.background_image, heroFallbackImage)})`
              : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform:
              activeSlide === index
                ? "scale(1) translateX(0)"
                : index > activeSlide
                ? "scale(1.1) translateX(100px)"
                : "scale(1.1) translateX(-100px)",
            opacity: activeSlide === index ? 1 : 0,
            transition: "all 800ms cubic-bezier(0.4, 0, 0.2, 1)",
            filter: activeSlide === index ? "none" : "blur(4px)",
            willChange: "transform, opacity, filter",
          }}
        />
      ))}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: isDark
            ? "rgba(10, 15, 26, 0.7)"
            : "rgba(250, 246, 240, 0.7)",
          zIndex: 1,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          opacity: 0.4,
          "&::before": {
            content: '""',
            position: "absolute",
            width: "2px",
            height: "2px",
            bgcolor: activeColor,
            borderRadius: "50%",
            boxShadow: `0 0 10px ${activeColor}`,
            animation: "float1 20s infinite linear",
            top: "20%",
            left: "10%",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: "3px",
            height: "3px",
            bgcolor: activeColor,
            borderRadius: "50%",
            boxShadow: `0 0 15px ${activeColor}`,
            animation: "float2 25s infinite linear",
            bottom: "30%",
            right: "20%",
          },
          "@keyframes float1": {
            "0%": { transform: "translate(0, 0)" },
            "25%": { transform: "translate(100px, -50px)" },
            "50%": { transform: "translate(200px, 50px)" },
            "75%": { transform: "translate(100px, 100px)" },
            "100%": { transform: "translate(0, 0)" },
          },
          "@keyframes float2": {
            "0%": { transform: "translate(0, 0)" },
            "25%": { transform: "translate(-50px, 100px)" },
            "50%": { transform: "translate(-100px, -50px)" },
            "75%": { transform: "translate(-50px, -100px)" },
            "100%": { transform: "translate(0, 0)" },
          },
        }}
      />

      <Box
        sx={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          px: { xs: 2, sm: 4, md: 6, lg: 8 },
          zIndex: 4,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 900,
            fontSize: {
              xs: "1.75rem",
              sm: "2.5rem",
              md: "3rem",
              lg: "3.5rem",
            },
            color: activeColor,
            mb: 2,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            transform: isTransitioning ? "translateY(20px)" : "translateY(0)",
            opacity: isTransitioning ? 0 : 1,
            transition: "all 600ms cubic-bezier(0.4, 0, 0.2, 1)",
            transitionDelay: isTransitioning ? "0ms" : "200ms",
          }}
        >
          {currentSlide?.title || ""}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "1rem", sm: "1.25rem" },
            maxWidth: { xs: "100%", md: 700 },
            mb: { xs: 3, md: 4 },
            color: isDark ? "rgba(240,244,248,0.8)" : "rgba(51,51,51,0.8)",
            transform: isTransitioning ? "translateY(20px)" : "translateY(0)",
            opacity: isTransitioning ? 0 : 1,
            transition: "all 600ms cubic-bezier(0.4, 0, 0.2, 1)",
            transitionDelay: isTransitioning ? "0ms" : "400ms",
          }}
        >
          {currentSlide?.subtitle || ""}
        </Typography>
        <Button
          variant="contained"
          size="large"
          component={
            isExternalLink ? "a" : isHashLink ? "button" : RouterLink
          }
          href={isExternalLink ? ctaLink : undefined}
          to={!isExternalLink && !isHashLink ? ctaLink : undefined}
          onClick={isHashLink ? handleHashCtaClick : undefined}
          startIcon={<AutoAwesome />}
          sx={{
            background: `linear-gradient(135deg, ${activeColor} 0%, ${activeGradientEnd} 100%)`,
            boxShadow: `0 4px 16px ${alpha(activeColor, 0.3)}`,
            fontWeight: 700,
            px: 4,
            py: 1.5,
            transform: isTransitioning ? "translateY(20px)" : "translateY(0)",
            opacity: isTransitioning ? 0 : 1,
            transition: "all 600ms cubic-bezier(0.4, 0, 0.2, 1)",
            transitionDelay: isTransitioning ? "0ms" : "600ms",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 8px 24px ${alpha(activeColor, 0.4)}`,
            },
          }}
        >
          {currentSlide?.cta_text || exploreProductsLabel}
        </Button>
        <Box
          component="img"
          src={resolveImageUrl(currentSlide?.background_image, heroFallbackImage)}
          alt={currentSlide?.title || ""}
          loading="eager"
          onError={(e) => withImageFallback(e, heroFallbackImage)}
          sx={{ display: "none" }}
        />
      </Box>

      <IconButton
        aria-label="Previous slide"
        onClick={onPrevSlide}
        sx={{
          position: "absolute",
          left: 12,
          top: "50%",
          transform: "translateY(-50%)",
          bgcolor: isDark ? alpha(backgroundPaper, 0.9) : backgroundDefault,
          backdropFilter: "blur(10px)",
          border: isDark
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
          color: textPrimary,
          zIndex: 5,
          "&:hover": {
            bgcolor: isDark ? alpha(backgroundPaper, 0.95) : backgroundPaper,
            border: `1px solid ${alpha(activeColor, 0.3)}`,
          },
        }}
      >
        <ArrowBackIosNew />
      </IconButton>

      <IconButton
        aria-label="Next slide"
        onClick={onNextSlide}
        sx={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          bgcolor: isDark ? alpha(backgroundPaper, 0.9) : backgroundDefault,
          backdropFilter: "blur(10px)",
          border: isDark
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
          color: textPrimary,
          zIndex: 5,
          "&:hover": {
            bgcolor: isDark ? alpha(backgroundPaper, 0.95) : backgroundPaper,
            border: `1px solid ${alpha(activeColor, 0.3)}`,
          },
        }}
      >
        <ArrowForwardIos />
      </IconButton>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 4,
          bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
          zIndex: 5,
        }}
      >
        <Box
          sx={{
            height: "100%",
            bgcolor: activeColor,
            width: `${((activeSlide + 1) / heroSlides.length) * 100}%`,
            transition: "width 5s linear",
            boxShadow: `0 0 10px ${activeColor}`,
          }}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 1.5,
          zIndex: 5,
        }}
      >
        {heroSlides.map((_, index) => (
          <IconButton
            key={index}
            size="small"
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => onSlideChange(index)}
            sx={{
              p: 0.5,
              transition: "all 300ms",
              "& svg": {
                fontSize: index === activeSlide ? "14px" : "10px",
                color:
                  index === activeSlide
                    ? activeColor
                    : isDark
                    ? "rgba(255,255,255,0.3)"
                    : "rgba(0,0,0,0.3)",
                filter:
                  index === activeSlide
                    ? `drop-shadow(0 0 8px ${activeColor})`
                    : "none",
              },
            }}
          >
            <FiberManualRecord />
          </IconButton>
        ))}
      </Box>
    </Box>
  );
};

export default ProductsHero;